import Cookies from 'js-cookie'
import Avatar from '@/models/Avatar'
import Api from '@/api'
import router from '@/routes'
import { resetIdleClock } from '@/utils/idleLogout'

const applyToken = (state, token) => {
  // Only persist the token fields to the cookie. The full login payload
  // includes the user object which can push the cookie past the ~4KB
  // per-cookie browser limit, causing the cookie to be silently rejected
  // and the user to appear logged out on refresh.
  const cookieToken = {
    access_token: token.access_token,
    token_type: token.token_type || 'Bearer',
    expires_in: token.expires_in != null ? token.expires_in : null,
    uuid:
      token.uuid != null
        ? token.uuid
        : token.token_id != null
        ? token.token_id
        : null,
    mfa_verified: !!token.mfa_verified,
  }

  Cookies.set('AUTH_TOKEN', cookieToken)
  state.token = token
  state.isAuthenticated = !!token.access_token
}

const defaultToken = () => ({
  access_token: null,
  expires_in: null,
  token_type: 'Bearer',
  uuid: null,
  mfa_verified: false,
})

const defaultMfaState = () => ({
  required: false,
  channel: null,
  otp_expires_at: null,
  token_id: null,
  attempts_remaining: null,
})

const defaultVerificationToken = () => ({
  token: null,
  expiresAt: null,
})

const coalesce = (value, fallback) =>
  value === undefined || value === null ? fallback : value

export default {
  namespaced: true,
  state: {
    user: null,
    isAuthenticated: false,
    isMfaVerified: false,
    token: defaultToken(),
    mfa: defaultMfaState(),
    verificationToken: defaultVerificationToken(),
    timestamp: new Date().getTime(),
  },

  getters: {
    user(state) {
      return state.user
    },
    isMfaRequired(state) {
      return state.mfa.required
    },
    canAccessDashboard(state, getters) {
      return !!(
        (state.user && state.user.user_type === 'business_admin') ||
        getters.isSupervisorTradie
      )
    },

    /**
     * A supervisor is a restricted business admin whose access is limited to the
     * Projects tab (plus their own Profile/Account). Backed by the `role` field
     * on the user object (see SUPERVISOR_ROLE_BACKEND.md).
     */
    isSupervisor(state) {
      return !!(
        state.user &&
        state.user.user_type === 'business_admin' &&
        state.user.role === 'supervisor'
      )
    },

    /**
     * The flat list of permission keys the authenticated user holds, as returned
     * by `/auth/me` (see ROLES_PERMISSIONS_BACKEND.md). Admins receive `["*"]`
     * which `hasPermission` treats as allow-all.
     *
     * Returns `null` (not `[]`) when the backend hasn't sent a `permissions`
     * array at all, so `hasPermission` can fail open for back-compat instead of
     * locking a user out of everything.
     */
    permissions(state) {
      if (state.user && Array.isArray(state.user.permissions)) {
        return state.user.permissions
      }
      return null
    },

    /**
     * Permission check used to gate nav, routes and buttons.
     * Usage: `hasPermission('files.delete')`.
     *
     * - `*` in the user's permission set means allow-all (admins).
     * - When the backend sent no `permissions` array, fail OPEN (return true) so
     *   older payloads keep working. An explicit empty array `[]` denies.
     */
    hasPermission(state, getters) {
      return (key) => {
        const perms = getters.permissions
        if (perms === null) return true
        return perms.includes('*') || perms.includes(key)
      }
    },

    /**
     * True when the user's role is project-scoped: they may only act inside their
     * assigned projects (the supervisor legacy role is one such role). Backed by
     * `is_project_scoped` on `/auth/me`, falling back to the legacy supervisor flag.
     */
    isProjectScoped(state, getters) {
      if (state.user && state.user.is_project_scoped != null) {
        return !!state.user.is_project_scoped
      }
      if (
        state.user &&
        state.user.user_type === 'business_admin' &&
        Array.isArray(state.user.assigned_project_ids) &&
        state.user.assigned_project_ids.length > 0
      ) {
        return true
      }
      return getters.isSupervisor || getters.isSupervisorTradie
    },

    /** Human-readable name of the user's role, e.g. "Site Manager". */
    roleName(state) {
      if (!state.user) return null
      return state.user.role_name || state.user.role || null
    },

    /**
     * True when the logged-in user is a tradie with supervisor web access.
     */
    isSupervisorTradie(state) {
      return !!(
        state.user &&
        state.user.user_type === 'tradie' &&
        state.user.is_supervisor
      )
    },

    /**
     * True when the user is a supervisor tradie AND currently has at least
     * one assigned project. A supervisor tradie with zero assigned projects
     * (never assigned one, or just removed from their last one) has nothing
     * to show on Timesheets/Projects/Schedule — those empty shells are what
     * "dashboard shows no projects" turned out to be — so nav items and
     * route guards for supervisor-only areas gate on this instead of the
     * raw isSupervisorTradie flag.
     */
    isActiveSupervisorTradie(state, getters) {
      return (
        getters.isSupervisorTradie && getters.supervisorProjectIds.length > 0
      )
    },

    /**
     * The set of project ids a project-scoped user (supervisor or custom role) is
     * allowed to access. Reads from `assigned_project_ids` (preferred) or derives
     * from an `assigned_projects` relation if the backend returns full objects.
     * For supervisor tradies, reads from `supervisor_project_ids`.
     */
    supervisorProjectIds(state) {
      if (!state.user) return []
      // Tradie supervisors use their own project id list
      if (
        state.user.user_type === 'tradie' &&
        Array.isArray(state.user.supervisor_project_ids)
      ) {
        return state.user.supervisor_project_ids
      }
      if (Array.isArray(state.user.assigned_project_ids)) {
        return state.user.assigned_project_ids
      }
      if (Array.isArray(state.user.assigned_projects)) {
        return state.user.assigned_projects
          .map((p) => (p && p.id != null ? p.id : p))
          .filter((id) => id != null)
      }
      return []
    },
  },

  mutations: {
    setUser(state, user) {
      state.user = user
    },

    clearUser(state) {
      state.user = null
    },

    // A fresh login. Resets the idle clock so a stale LAST_ACTIVITY_AT from a
    // previous session can't sign the user straight back out.
    setToken(state, token) {
      if (!token) {
        return
      }

      resetIdleClock()
      applyToken(state, token)
    },

    // Rehydrating a saved session on page load — must NOT reset the idle
    // clock, or reopening a tab after the timeout would never log out.
    restoreToken(state, token) {
      if (!token) {
        return
      }

      applyToken(state, token)
    },

    clearToken(state) {
      Cookies.remove('AUTH_TOKEN')
      state.token = defaultToken()
      state.isAuthenticated = false
      state.isMfaVerified = false
      state.mfa = defaultMfaState()
    },

    // set MFA state
    setMfaState(state, payload) {
      state.mfa = {
        ...defaultMfaState(),
        ...payload,
        required: true,
      }
      state.isMfaVerified = false
    },

    // clear MFA state
    clearMfaState(state) {
      state.mfa = defaultMfaState()
    },

    // set mfa verified
    setMfaVerified(state, verified) {
      state.isMfaVerified = !!verified
      if (verified) {
        // set default mfa state and state token
        state.mfa = defaultMfaState()
        state.token = {
          ...state.token,
          mfa_verified: true,
        }
        Cookies.set('AUTH_TOKEN', state.token)
      }
    },
    changeTimestamp(state) {
      state.timestamp = new Date().getTime()
    },

    setUserAvatar(state, avatar) {
      if (avatar instanceof Avatar) {
        state.user.avatar = avatar
      } else {
        state.user.avatar = new Avatar(avatar)
      }
    },

    setVerificationToken(state, token) {
      if (token) {
        state.verificationToken = token
      }
    },

    clearVerificationToken(state) {
      state.verificationToken = defaultVerificationToken()
    },
  },

  actions: {
    init({ commit, state }) {
      const token = Cookies.getJSON('AUTH_TOKEN')
      if (token && token.access_token) {
        commit('restoreToken', token)
        // Trust the cookie's mfa_verified flag on rehydrate. The router guard
        // still calls /auth/me which is authoritative and will flip this back
        // to false if the backend disagrees.
        state.isMfaVerified = !!token.mfa_verified
      }
    },

    async getAuthenticatedUser({ commit, state }) {
      await Api.get('/auth/me')
        .then((res) => {
          const user = res.data.data || res.data
          commit('setUser', user)

          if (res.data.mfa_required) {
            commit('setMfaState', {
              channel: res.data.mfa_channel || null,
              token_id: res.data.token_id || null,
            })

            commit('setMfaVerified', false)
          } else {
            // clear MFA state
            commit('clearMfaState')

            // backend says MFA not required, mark verified for router guard
            commit('setMfaVerified', true)
          }
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            commit('clearToken')
            commit('clearUser')
          }

          return Promise.reject(err)
        })
    },

    logout({ commit }) {
      // Clear local state even if the revoke call fails (e.g. the token has
      // already expired server-side) — otherwise the user is stuck logged in
      // with a dead token.
      Api.post('/auth/logout')
        .catch(() => {})
        .then(() => {
          commit('clearToken')
          commit('clearUser')
          commit('clearVerificationToken')
          localStorage.clear()
          // clear other states as deemed necessary

          // Hard-navigate to /login. The old `router.replace()` + `router.go()`
          // raced: go() reloaded the *current* route before replace committed,
          // so a first-login business admin logging out landed back on
          // /change-password with no session. A full page load on the resolved
          // login href also skips the in-between guard run (and its /auth/me
          // 401 with the token now gone).
          const { href } = router.resolve({ name: 'auth.login-page' })
          window.location.assign(href)
        })
    },

    async sendMfa({ commit, state }, payload = {}) {
      const currentMfa = state.mfa || defaultMfaState()
      const tokenUuid = state.token ? state.token.uuid : null
      const requestBody = {
        token_id: coalesce(currentMfa.token_id, tokenUuid),
        ...(payload || {}),
      }
      const { data } = await Api.post('auth/mfa/send', requestBody)
      const responseData = data && data.data ? data.data : {}

      commit('setMfaState', {
        channel: coalesce(responseData.mfa_channel, currentMfa.channel),
        otp_expires_at: coalesce(
          responseData.otp_expires_at,
          currentMfa.otp_expires_at,
        ),
        token_id: coalesce(responseData.token_id, currentMfa.token_id),
        attempts_remaining: coalesce(
          responseData.attempts_remaining,
          currentMfa.attempts_remaining,
        ),
      })
    },

    async verifyMfa({ commit }, payload) {
      const { data } = await Api.post('auth/mfa/verify', payload)
      const responseData = data && data.data ? data.data : {}
      const token = responseData.token || responseData

      if (token && token.access_token) {
        commit('setToken', token)
      }

      // treat any successful verify as MFA complete; backend returns mfa_required=false anyway
      commit('setMfaVerified', true)
      commit('clearMfaState')

      if (responseData.user) {
        commit('setUser', responseData.user)
      }

      return responseData
    },

    handleMfaRequired({ commit }, payload = {}) {
      commit('setMfaState', {
        channel: coalesce(payload.mfa_channel, null),
        otp_expires_at: coalesce(payload.otp_expires_at, null),
        token_id: coalesce(payload.token_id, null),
        attempts_remaining: coalesce(payload.attempts_remaining, null),
      })
      router.replace({ name: 'auth.mfa-page' }).catch(() => {})
    },
    async changeAvatar({ commit }, formData) {
      const { data } = await Api.post('auth/profile/avatar', formData)
      commit('setUserAvatar', data.data)
    },

    async changeTradieAvatar({ commit }, formData) {
      await Api.post('auth/profile/avatar', formData)
    },

    async updateUser({ commit }, formData) {
      const { data } = await Api.put('auth/profile', formData)
      commit('setUser', data.data)
      return data
    },

    /**
     * Accept one of the consent types tracked on the user record
     * ('signup' | 'ai' | 'manual_upload'). Consents are one-way — the API has
     * no revoke path. Commits the refreshed user so `signup_consent_given` etc.
     * flip immediately across the app.
     */
    async acceptConsent({ commit, state }, type) {
      const { data } = await Api.post('auth/profile/accept-consent', { type })
      if (data && data.data) {
        // The accept-consent endpoint serialises the user without eager-loading
        // `avatar`/`resume`, so those keys are absent from the response. Merge
        // over the current user rather than replacing it, or the avatar would
        // vanish from the UI until the next full /auth/me.
        commit('setUser', { ...(state.user || {}), ...data.data })
      }
      return data
    },

    async deleteAccount(
      { commit },
      { replacementBaUserId = null, scheduled = false } = {},
    ) {
      const payload = replacementBaUserId
        ? { data: { replacement_ba_user_id: replacementBaUserId } }
        : {}
      const { data } = await Api.delete('auth/account', payload)
      commit('clearToken')
      commit('clearUser')
      commit('clearVerificationToken')
      // A tradie either gets a notice period (they have a company) or is
      // removed straight away (they don't) - the API says which, and the login
      // page explains what happens next.
      let deletion
      if (scheduled) {
        const outcome = data && data.data
        deletion =
          outcome && outcome.scheduled === false ? 'deleted' : 'scheduled'
      }
      router.replace({
        name: 'auth.login-page',
        query: deletion ? { deletion } : undefined,
      })
    },

    async getVerificationToken({ commit }, formData) {
      const { data } = await Api.post(
        'auth/account/verification-token',
        formData,
      )
      commit('setVerificationToken', data.data)
    },

    async requestChangeEmail({ commit }, formData) {
      await Api.post('auth/change/email', formData)
    },

    async verifyChangeEmail({ state, commit }, formData) {
      const { data } = await Api.post('auth/change/email/verify', formData)
      commit('clearVerificationToken')
      state.user.email = formData.email
      commit('setUser', data.data)
    },

    async requestChangePhone({ commit }, formData) {
      await Api.post('auth/change/phone-number', formData)
    },

    async verifyChangePhone({ state, commit }, formData) {
      const { data } = await Api.post(
        'auth/change/phone-number/verify',
        formData,
      )
      commit('clearVerificationToken')
      state.user.phone_number = formData.phone_number
      commit('setUser', data.data)
    },

    async changePassword({ commit }, formData) {
      await Api.post('auth/change/password', formData)
      commit('clearVerificationToken')
    },

    async resendVerificationCode({ commit }, field) {
      await Api.post('auth/verification/resend', { via: field })
    },

    async submitVerificationCode({ commit }, formData) {
      const { data } = await Api.post('auth/verification/verify', formData)
      commit('setUser', data.data)
    },
  },
}
